import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Typography } from '@/components/ui/typography'
import { extractErrorMessage, toast } from '@/lib/toast'
import {
  useActiveSendBatches,
  useEnqueueSendBatch,
  useRefreshLeads,
  useSendBatchPreview,
} from '@/hooks/use-deal-send-batches'
import { MUTED_TEXT } from '@/features/deal-review/review-tones'
import {
  confirmCopy,
  queuedMessage,
  SEND_TOAST_POSITION,
} from '@/features/deal-review/send-batch'

export interface SendBatchConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  leads: { id: string; version: number }[]
  onQueued: () => void
}

export function SendBatchConfirmDialog({
  open,
  onOpenChange,
  leads,
  onQueued,
}: SendBatchConfirmDialogProps) {
  const preview = useSendBatchPreview(leads, open)
  const active = useActiveSendBatches()
  const enqueue = useEnqueueSendBatch()
  const refreshLeads = useRefreshLeads()

  // The preview may have found leads changed or gone; the board catches up on close.
  const changeOpen = (next: boolean) => {
    if (!next) refreshLeads()
    onOpenChange(next)
  }

  const anotherRunning = Boolean(
    active.data &&
    (active.data.running !== null || active.data.queued.length > 0)
  )
  const loading = preview.isFetching || (preview.isPending && !preview.isError)
  const copy =
    !loading && preview.data
      ? confirmCopy(preview.data, leads.length, anotherRunning)
      : null

  const confirm = () =>
    enqueue.mutate(
      { leads },
      {
        onSuccess: (result) => {
          toast.success(queuedMessage(result, preview.data), {
            position: SEND_TOAST_POSITION,
          })
          onOpenChange(false)
          onQueued()
        },
        onError: (error) =>
          toast.error(
            extractErrorMessage(error) ?? 'The send could not be queued.',
            { position: SEND_TOAST_POSITION }
          ),
      }
    )

  return (
    <AlertDialog open={open} onOpenChange={changeOpen}>
      <AlertDialogContent
        // A ready dialog has no description; an explicit undefined stops Radix pointing at none.
        {...(copy && !copy.description && { 'aria-describedby': undefined })}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>
            {copy?.title ?? 'Send to DealCloud?'}
          </AlertDialogTitle>
          {!loading && preview.isError ? (
            <AlertDialogDescription>
              The selection could not be checked. Nothing was sent.
            </AlertDialogDescription>
          ) : copy?.description ? (
            <AlertDialogDescription>{copy.description}</AlertDialogDescription>
          ) : copy ? null : (
            <AlertDialogDescription className="sr-only">
              Checking which of the selected deals can be sent.
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>

        {loading && (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-4 w-4/5" />
            <Skeleton className="h-4 w-3/5" />
          </div>
        )}

        {copy &&
          (copy.impliedLine ||
            copy.queueLine ||
            copy.skippedLines.length > 0) && (
            <div className="flex flex-col gap-2">
              {[copy.impliedLine, copy.queueLine].map(
                (line) =>
                  line && (
                    <Typography
                      key={line}
                      as="p"
                      variant="body-sm"
                      className="text-sm text-primary"
                    >
                      {line}
                    </Typography>
                  )
              )}
              {copy.skippedIntro && (
                <Typography
                  as="p"
                  variant="body-sm"
                  className="text-sm text-primary"
                >
                  {copy.skippedIntro}
                </Typography>
              )}
              {copy.skippedLines.length > 0 && (
                <ul className="flex flex-col gap-0.5 leading-none">
                  {copy.skippedLines.map((line) => (
                    <li key={line}>
                      <Typography
                        as="span"
                        variant="body-sm"
                        className={MUTED_TEXT}
                      >
                        {line}
                      </Typography>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

        <AlertDialogFooter>
          <AlertDialogCancel>
            {copy && copy.action === null ? 'Close' : 'Cancel'}
          </AlertDialogCancel>
          {loading ? (
            <AlertDialogAction disabled>Checking…</AlertDialogAction>
          ) : preview.isError ? (
            // A plain button: AlertDialogAction would close the dialog.
            <Button variant="outline" onClick={() => void preview.refetch()}>
              Try again
            </Button>
          ) : (
            copy?.action && (
              <AlertDialogAction
                disabled={enqueue.isPending}
                aria-busy={enqueue.isPending}
                onClick={(event) => {
                  // Stays open until the enqueue settles, so an error keeps the choice on screen.
                  event.preventDefault()
                  confirm()
                }}
              >
                {enqueue.isPending ? 'Queuing…' : copy.action}
              </AlertDialogAction>
            )
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
